import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Model } from "survey-core";
import '../../i18n';
import usePageTitle from '../../Hooks/usePageTitle';
import useBodyClass from '../../Hooks/useBodyClass';
import SurveyComponent from 'ad-web-ui-library';
import './captureforms.scss';
import Button from '../UI/Buttons/Button';
import RCH from './RHC';
import useResize from '../../Hooks/useResize';
import usePostMessage from '../../Hooks/usePostMessage';
import { APIGet, APIPost } from '../API/APIRequest';
import { GenericErrorBox } from '../UI/ErrorBox/GenericErrorBox';
import { GetADToken } from '../Utilities/TokenManager';
import { useAuth } from '../Authentication/AuthenticationProvider';
import { GetCurrentBrand } from '../Configuration/ConfigurationManager';

const AUTO_SAVE_DELAY = 2000; // 2 second debounce for auto-save

/**
 * Reads the form identifier from the query string. The canonical param is "surveycode"
 * (inf_SURVEY.SURVEYCODE), matching the legacy V1 /questionnaireform?surveycode=X links.
 * Matching is case-insensitive so ?surveyCode= and ?SurveyCode= both work. Returns null
 * when not present.
 */
const getSurveyCodeParam = (searchParams) => {
    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === "surveycode" && value) return value;
    }
    return null;
};

const CaptureForms = (props) => {
    const { t } = useTranslation();
    const { environment, friendlyUrl } = useParams();
    const [searchParams] = useSearchParams();

    // URL parameters
    // The form is addressed by ?surveycode= (inf_SURVEY.SURVEYCODE), matching the legacy V1
    // /questionnaireform?surveycode=X links. This value flows into the internal formId state
    // and the getformbyid API, which still use the "formId" name.
    const formIdParam = getSurveyCodeParam(searchParams);
    const returnUrl = searchParams.get("returnUrl");
    const campaignCode = searchParams.get("campaigncode") || searchParams.get("campaignCode");
    const isEmbeddedParam = searchParams.get("embedded") === "true";
    const isEmbedded = props?.isEmbedded || (isEmbeddedParam ? { disableRCH: false } : null);

    // State
    const [formId, setFormId] = useState(formIdParam);
    const [formSchema, setFormSchema] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [open, setOpen] = useState(false);
    const [buttonState, setButtonState] = useState("initial");
    const [submitted, setSubmitted] = useState(false);
    const [autoSaveRestored, setAutoSaveRestored] = useState(false);
    const [redirectingToLogin, setRedirectingToLogin] = useState(false);

    // Authentication state — questionnaire forms require an authenticated + activated user.
    // Auth is handled inside this component (not PrivateRoute) so we can also render the
    // "not activated" message rather than only redirecting.
    const auth = useAuth();
    const adtoken = GetADToken();
    const { isLoading: authLoading, userData } = auth ?? {};
    const isAnonymous = !adtoken && (!userData || userData?.anon);
    const isNotActivated = adtoken && adtoken.isactivated !== undefined && adtoken.isactivated === 0;

    // Refs
    const surveyModelRef = useRef(null);
    const surveyComponentRef = useRef(null);
    const autoSaveTimerRef = useRef(null);

    // Hooks
    const windowSize = useResize();
    const isMobile = windowSize < 1000;

    useBodyClass(['captureforms', props.brandingElement?.bodyClass]);
    usePageTitle(t('forms.pagetitle', 'Form'));

    // PostMessage for embedded mode
    const targetUrl = sessionStorage.getItem("ReturnUrl") || returnUrl;
    usePostMessage({
        targetOrigin: targetUrl,
        containerClass: 'container-wrapper',
        messageType: 'embeddedResize',
    });

    // ---------------------------------------------------------------------
    // Auto-Save: Save form data to sessionStorage (Pattern 2)
    // ---------------------------------------------------------------------
    const autoSaveKey = `formAutoSave_${formId}`;

    const saveToStorage = useCallback((data) => {
        try {
            sessionStorage.setItem(autoSaveKey, JSON.stringify(data));
        } catch (e) {
            // Storage quota exceeded — fail silently
            console.warn("Auto-save failed: storage quota exceeded");
        }
    }, [autoSaveKey]);

    const restoreFromStorage = useCallback((survey) => {
        try {
            const saved = sessionStorage.getItem(autoSaveKey);
            if (saved) {
                const data = JSON.parse(saved);
                // Only restore if there's actual data (not an empty object)
                if (data && Object.keys(data).length > 0) {
                    survey.data = data;
                    setAutoSaveRestored(true);
                    return true;
                }
            }
        } catch (e) {
            // Corrupted data — clear it
            sessionStorage.removeItem(autoSaveKey);
        }
        return false;
    }, [autoSaveKey]);

    const clearAutoSave = useCallback(() => {
        sessionStorage.removeItem(autoSaveKey);
    }, [autoSaveKey]);

    // ---------------------------------------------------------------------
    // Auth: redirect anonymous users to the middleware login page.
    // Builds {authmiddlewareurl}/{hostname}/login?ReturnUrl={current url} so the
    // user is returned to this exact form (with query params) after signing in.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || !isAnonymous || redirectingToLogin) return;

        setRedirectingToLogin(true);

        GetCurrentBrand(null)
            .then((brand) => {
                let host = window.location.hostname;
                if (host === 'localhost') {
                    host = 'ad-frontend.abasoft.co.uk';
                }

                const returnTo = window.location.protocol + "//" + window.location.host
                    + window.location.pathname + window.location.search;

                const loginURL = `${brand.authmiddlewareurl}/${host}/login?ReturnUrl=`
                    + encodeURIComponent(returnTo);

                window.location.href = loginURL;
            })
            .catch((error) => {
                console.error("Redirection to middleware login failed:", error);
                setRedirectingToLogin(false);
                setErrors([t('error.unexpected', 'Something went wrong. Please try again.')]);
            });
    }, [authLoading, isAnonymous, redirectingToLogin, t]);

    // ---------------------------------------------------------------------
    // Resolve friendly URL slug to formId (if route param is used)
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || isAnonymous || isNotActivated || redirectingToLogin) return; // Wait for auth
        if (!friendlyUrl || formId) return; // Skip if no slug or formId already set

        APIGet({
            controller: "forms",
            action: "getformbyfriendlyurl",
            identifier: friendlyUrl,
            environment: environment
        })
            .then((response) => {
                if (response.data?.success && response.data?.formId) {
                    setFormId(response.data.formId.toString());
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                    setIsLoading(false);
                }
            })
            .catch((error) => {
                console.error("Failed to resolve friendly URL:", error);
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    }, [friendlyUrl, formId, environment, t, authLoading, isAnonymous, isNotActivated, redirectingToLogin]);

    // ---------------------------------------------------------------------
    // Fetch form schema from API (Pattern 3: Skeleton loading)
    // Gated behind auth resolution so we don't call the API for users who will be
    // redirected to login or blocked with the activation message.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (authLoading || isAnonymous || isNotActivated || redirectingToLogin) return; // Wait for auth
        if (!formId) {
            // Only show error if there's no friendlyUrl either (still resolving)
            if (!friendlyUrl) {
                setErrors([t('forms.error.noformid', 'No form ID provided')]);
                setIsLoading(false);
            }
            return;
        }

        APIGet({
            controller: "forms",
            action: "getformbyid",
            identifier: formId,
            environment: environment
        })
            .then((response) => {
                const schema = response.data?.surveyData;
                if (schema) {
                    setFormSchema(schema);
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                }
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Failed to load form schema:", error);
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    }, [formId, environment, t, authLoading, isAnonymous, isNotActivated, redirectingToLogin]);

    // ---------------------------------------------------------------------
    // Create SurveyJS model when schema is loaded
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (formSchema && !surveyModelRef.current) {
            const survey = new Model(formSchema);
            surveyModelRef.current = survey;

            // Restore auto-saved data if available
            restoreFromStorage(survey);

            // Listen for page changes
            survey.onCurrentPageChanged.add((sender) => {
                setPageNumber(sender.currentPageNo);
            });

            // Load dynamic endpoint data (country dropdowns, company lookups)
            survey.onAfterRenderQuestion.add((sender, options) => {
                const q = options.question;
                const dataEndpoint = q.jsonObj?.dataEndpoint || q.dataEndpoint;

                if (q.getType() === 'dropdown' && dataEndpoint) {
                    const apiProps = {
                        controller: "forms",
                        action: dataEndpoint,
                        identifier: formId,
                        environment: environment,
                        data: ""
                    };

                    // Company lookup requires extra params
                    if (dataEndpoint === "getformcompanies") {
                        apiProps.data = {
                            isDisplayAddrLine1: !!q.jsonObj?.isDisplayAddrLine1,
                            isDisplayPostCode: !!q.jsonObj?.isDisplayPostCode,
                            isDisplayTown: !!q.jsonObj?.isDisplayTown,
                            isDisplayCountry: !!q.jsonObj?.isDisplayCountry,
                        };
                    }

                    APIGet(apiProps)
                        .then((response) => {
                            const data = response.data?.choices;
                            if (!data) return;

                            if (dataEndpoint === "getformcompanies") {
                                q.choices = data.map((item) => {
                                    const addressInfo = [item?.AddressLine1, item?.PostCode, item?.Town, item?.Country].filter(Boolean);
                                    const details = addressInfo.length > 0 ? ` (${addressInfo.join(", ")})` : "";
                                    return { value: item.Name, text: `${item.Name}${details}` };
                                });
                            } else {
                                q.choices = data.map((item) => ({
                                    value: item.name,
                                    text: item.name,
                                }));
                            }
                        })
                        .catch((err) => console.error("Endpoint load error:", err));
                }
            });

            // Auto-save on value change (debounced)
            survey.onValueChanged.add(() => {
                if (autoSaveTimerRef.current) {
                    clearTimeout(autoSaveTimerRef.current);
                }
                autoSaveTimerRef.current = setTimeout(() => {
                    saveToStorage(survey.data);
                }, AUTO_SAVE_DELAY);
            });

            // Force re-render to show the form
            setPageNumber(0);
        }
    }, [formSchema, restoreFromStorage, saveToStorage]);

    // Cleanup auto-save timer on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, []);

    // ---------------------------------------------------------------------
    // Form Submission (Pattern 1: Inline error + retry)
    // ---------------------------------------------------------------------
    const handleSubmit = (e) => {
        e.preventDefault();
        const survey = surveyModelRef.current;
        if (!survey) return;

        // Validate entire form
        const isValid = survey.validate(true);
        if (!isValid) {
            // Scroll to first error
            setTimeout(() => {
                const firstError = document.querySelector(".sd-question--error input, .sd-question--error textarea");
                if (firstError) {
                    firstError.scrollIntoView({ behavior: "smooth", block: "center" });
                    firstError.focus({ preventScroll: true });
                }
            }, 200);
            return;
        }

        setButtonState("loading");
        setErrors(null);

        // Prefix answers (answer codes for backward compatibility)
        if (surveyComponentRef.current?.prefixAnswers) {
            surveyComponentRef.current.prefixAnswers();
        }

        // Collect form data
        const surveyData = surveyComponentRef.current?.getSurveyData
            ? surveyComponentRef.current.getSurveyData()
            : survey.data;

        // Build submission payload
        const formData = {};
        for (let key in surveyData) {
            const value = surveyData[key];
            if (Array.isArray(value)) {
                formData[key] = value.join(",");
            } else if (value !== null && value !== undefined) {
                formData[key] = String(value);
            }
        }

        const submitPayload = {
            formId: parseInt(formId),
            surveyCode: formSchema?.formSettings?.surveyCode || formId,
            formData: formData,
            returnUrl: returnUrl,
            thankYouPageUrl: formSchema?.formSettings?.thankYouPageUrl || null,
            campaignCode: campaignCode || null
        };

        APIPost({
            controller: "forms",
            action: "submit",
            environment: environment,
            data: submitPayload,
            content_type: "json",
            headers: [{ key: "UserToken", value: GetADToken()?.token ?? "" }]
        })
            .then((response) => {
                const result = response.data;
                if (result.success) {
                    // Clear auto-save on success
                    clearAutoSave();
                    setSubmitted(true);

                    // Handle redirect
                    const redirectUrl = result.redirectUrl;
                    if (redirectUrl) {
                        if (isEmbedded) {
                            // Embedded mode: notify parent via postMessage
                            window.parent.postMessage(
                                { type: "formComplete", success: true, redirectUrl },
                                targetUrl || "*"
                            );
                        } else {
                            window.location.href = redirectUrl;
                        }
                    } else if (isEmbedded) {
                        window.parent.postMessage(
                            { type: "formComplete", success: true },
                            targetUrl || "*"
                        );
                    }
                } else {
                    setErrors(result.errors || [t('forms.error.submitfailed', 'Submission failed. Please try again.')]);
                    setButtonState("initial");
                }
            })
            .catch((error) => {
                console.error("Form submission failed:", error);
                setErrors([t('forms.error.submitfailed', 'Submission failed. Please try again.')]);
                setButtonState("initial");
            });
    };

    // ---------------------------------------------------------------------
    // Retry handler for schema load failure
    // ---------------------------------------------------------------------
    const handleRetry = () => {
        setErrors(null);
        setIsLoading(true);
        setFormSchema(null);
        surveyModelRef.current = null;

        APIGet({
            controller: "forms",
            action: "getformbyid",
            identifier: formId,
            environment: environment
        })
            .then((response) => {
                const schema = response.data?.surveyData;
                if (schema) {
                    setFormSchema(schema);
                } else {
                    setErrors([t('forms.error.noschema', 'Form not found')]);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setErrors([t('forms.error.loadfailed', 'Failed to load form. Please try again.')]);
                setIsLoading(false);
            });
    };

    // ---------------------------------------------------------------------
    // Render: Auth resolving or redirecting to login (State 1)
    // Show the skeleton while auth resolves or while we redirect anonymous
    // users to the middleware login page.
    // ---------------------------------------------------------------------
    if (authLoading || isAnonymous || redirectingToLogin) {
        return (
            <div className="container-wrapper" data-testid="captureforms-auth-loading">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="skeleton-block skeleton-title"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-button"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: User not activated (State 2)
    // Logged in but account not activated — block access, no schema fetch.
    // ---------------------------------------------------------------------
    if (isNotActivated) {
        return (
            <div className="container-wrapper" data-testid="captureforms-not-activated">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <GenericErrorBox
                                    errors={[t('forms.error.notactivated', 'Please activate your account to access this form. Check your email for the activation link.')]}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: Loading state (Pattern 3: Skeleton)
    // ---------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="container-wrapper" data-testid="captureforms-loading">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="skeleton-block skeleton-title"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-field"></div>
                                <div className="skeleton-block skeleton-button"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: Error state (no form loaded)
    // ---------------------------------------------------------------------
    if (!formSchema && errors) {
        return (
            <div className="container-wrapper" data-testid="captureforms-error">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <GenericErrorBox errors={errors} />
                                <Button onClick={handleRetry} buttonSize="medium">
                                    {t('forms.retry', 'Try Again')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ---------------------------------------------------------------------
    // Render: Success state (form submitted)
    // ---------------------------------------------------------------------
    if (submitted) {
        return (
            <div className="container-wrapper" data-testid="captureforms-success">
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <p>{t('forms.success', 'Thank you. Your form has been submitted successfully.')}</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    const survey = surveyModelRef.current;
    if (!survey) return null;

    // Determine whether the RHC (right-hand column) has any content to show for the
    // current page. Mirrors the logic in the RHC component so we don't render an empty
    // <section> wrapper (which would otherwise leave a blank column on the page).
    const rhcPages = formSchema?.pages;
    const hasRhcContent = typeof pageNumber === 'number'
        ? !!rhcPages?.[pageNumber]?.htmlRHC
        : !!rhcPages?.some(p => p?.htmlRHC);

    // ---------------------------------------------------------------------
    // Render: Form
    // ---------------------------------------------------------------------
    return (
        <div className="container-wrapper two-columns" data-testid="captureforms-form">

            {/* Mobile RHC toggle (Pattern 6: aria-expanded) */}
            {!isEmbedded?.disableRCH && isMobile && hasRhcContent && (
                <section>
                    <div className="container">
                        <div className="item-container">
                            <div className="item-wrapper">
                                <div className="two-columns-row">
                                    <button
                                        onClick={() => setOpen(!open)}
                                        className="text-button"
                                        aria-expanded={open}
                                        aria-controls="rhc-expandable"
                                    >
                                        {open
                                            ? t('forms.hideinfo', 'Hide information')
                                            : t('forms.showinfo', 'View more information')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        id="rhc-expandable"
                        className={`expandable ${open ? "open" : ""}`}
                        aria-hidden={!open}
                    >
                        <div>
                            <RCH surveyJson={formSchema} pageNo={pageNumber} />
                        </div>
                    </div>
                </section>
            )}

            {/* Form Section */}
            <section>
                <div className="container">
                    {/* Auto-save restored message */}
                    {autoSaveRestored && (
                        <div className="item-container">
                            <div className="item-wrapper">
                                <p className="info-message" role="status" aria-live="polite">
                                    {t('forms.restored', 'Your previous answers have been restored.')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Inline error (Pattern 1) */}
                    {errors && (
                        <div className="item-container" aria-live="polite">
                            <div className="item-wrapper">
                                <GenericErrorBox errors={errors} />
                            </div>
                        </div>
                    )}

                    {/* SurveyJS Form (using shared library) */}
                    <div className="item-container">
                        <div className="item-wrapper">
                            <SurveyComponent
                                ref={surveyComponentRef}
                                survey={survey}
                                formId={formId}
                                apiGet={APIGet}
                                controller="forms"
                                environment={environment}
                                onComplete={(data) => {
                                    // SurveyJS internal completion - not used, we handle submit manually
                                }}
                            />
                        </div>
                    </div>

                    {/* Submit Button — capture forms are single-page, so no Previous/Next navigation */}
                    <div className="item-container">
                        <div className="item-wrapper one-row">
                            {buttonState === "loading" ? (
                                <Button
                                    isDisabled="true"
                                    hasIcon="updating"
                                    buttonSize="large"
                                    btnType="primary"
                                    data-testid="captureforms-submit-button"
                                >
                                    {t('forms.submitting', 'Submitting...')}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    buttonSize="large"
                                    btnType="primary"
                                    onClick={handleSubmit}
                                    data-testid="captureforms-submit-button"
                                >
                                    {t('forms.submit', 'Submit')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop RHC (side column) */}
            {!isEmbedded?.disableRCH && !isMobile && hasRhcContent && (
                <section>
                    <RCH surveyJson={formSchema} pageNo={pageNumber} />
                </section>
            )}
        </div>
    );
};

export default CaptureForms;
